import { cn } from "@/utils/helpers";

const PIPELINE_STOPS = ["Concept", "Production", "Live"] as const;
// A quick post never claims an approval (Figma 08, 13 Oct): its stops are the files, then Live.
const QUICK_STOPS = ["Files", "Live"] as const;
// The client's side never hears "Production" (DESIGN.md "The status banner").
const GUEST_PIPELINE_STOPS = ["Idea and brief", "With the team", "Live"] as const;
const GUEST_QUICK_STOPS = ["With the team", "Live"] as const;

/**
 * The three-stage track (DESIGN.md "Three stages"): equal bars, done in the
 * secondary ink, the current stage in the accent, the rest in the hairline. No
 * "2 of 7" count. A quick post keeps its own two stops.
 */
export default function StageTrack({
  stageIndex,
  stops = 3,
  size = "card",
  audience = "team",
  className,
}: {
  stageIndex: number;
  stops?: 2 | 3;
  size?: "rail" | "card";
  /** Who hears the label: the client gets their own stage words. */
  audience?: "team" | "client";
  className?: string;
}) {
  const guest = audience === "client";
  const labels: readonly string[] =
    stops === 2 ? (guest ? GUEST_QUICK_STOPS : QUICK_STOPS) : guest ? GUEST_PIPELINE_STOPS : PIPELINE_STOPS;
  const current = Math.max(0, Math.min(stageIndex, labels.length - 1));
  const rail = size === "rail";
  return (
    <span
      role="img"
      aria-label={`${labels[current]}, stage ${current + 1} of ${labels.length}`}
      className={cn("flex w-full", rail ? "gap-[2px]" : "gap-1", className)}
    >
      {labels.map((label, index) => (
        <span
          key={`${index}-${label}`}
          aria-hidden="true"
          className={cn("min-w-0 flex-1", rail ? "h-[2px]" : "h-[3px]")}
          style={{
            backgroundColor:
              index < current
                ? rail
                  ? "var(--co-muted)"
                  : "var(--co-text2)"
                : index === current
                  ? rail
                    ? "var(--co-accent-ink)"
                    : "var(--co-accent)"
                  : "var(--co-line2)",
          }}
        />
      ))}
    </span>
  );
}
